// Adapted from: https://github.com/harthur/color-convert/
/* Copyright (c) 2011 Heather Arthur <fayearthur@gmail.com>

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

/* MIT license */


export const xyz2rgb = function(xyz) {
  var x = xyz[0] / 100,
      y = xyz[1] / 100,
      z = xyz[2] / 100,
      r, g, b;

  r = (x * 3.2406) + (y * -1.5372) + (z * -0.4986);
  g = (x * -0.9689) + (y * 1.8758) + (z * 0.0415);
  b = (x * 0.0557) + (y * -0.2040) + (z * 1.0570);

  // assume sRGB
  r = r > 0.0031308 ? ((1.055 * Math.pow(r, 1.0 / 2.4)) - 0.055)
    : r = (r * 12.92);

  g = g > 0.0031308 ? ((1.055 * Math.pow(g, 1.0 / 2.4)) - 0.055)
    : g = (g * 12.92);

  b = b > 0.0031308 ? ((1.055 * Math.pow(b, 1.0 / 2.4)) - 0.055)
    : b = (b * 12.92);

  r = Math.min(Math.max(0, r), 1);
  g = Math.min(Math.max(0, g), 1);
  b = Math.min(Math.max(0, b), 1);

  return [r * 255, g * 255, b * 255];
};

export const xyz2lab = function(xyz) {
  var x = xyz[0],
      y = xyz[1],
      z = xyz[2],
      l, a, b;

  x /= 95.047;
  y /= 100;
  z /= 108.883;

  x = x > 0.008856 ? Math.pow(x, 1/3) : (7.787 * x) + (16 / 116);
  y = y > 0.008856 ? Math.pow(y, 1/3) : (7.787 * y) + (16 / 116);
  z = z > 0.008856 ? Math.pow(z, 1/3) : (7.787 * z) + (16 / 116);

  l = (116 * y) - 16;
  a = 500 * (x - y);
  b = 200 * (y - z);

  return [l, a, b];
};

export const lab2xyz = function(lab) {
  var l = lab[0],
      a = lab[1],
      b = lab[2],
      x, y, z, y2;

  if(l <= 8) {
    y = (l * 100) / 903.3;
    y2 = (7.787 * (y / 100)) + (16 / 116);
  } else {
    y = 100 * Math.pow((l + 16) / 116, 3);
    y2 = Math.pow(y / 100, 1/3);
  }

  x = x / 95.047 <= 0.008856 ? x = (95.047 * ((a / 500) + y2 - (16 / 116))) / 7.787 : 95.047 * Math.pow((a / 500) + y2, 3);

  z = z / 108.883 <= 0.008859 ? z = (108.883 * (y2 - (b / 200) - (16 / 116))) / 7.787 : 108.883 * Math.pow(y2 - (b / 200), 3);

  return [x, y, z];
};

export const lab2lch = function(lab) {
  var l = lab[0],
      a = lab[1],
      b = lab[2],
      hr, h, c;

  hr = Math.atan2(b, a);
  h = hr * 360 / 2 / Math.PI;
  if(h < 0) {
    h += 360;
  }
  c = Math.sqrt(a * a + b * b);
  return [l, c, h];
};

export const lab2rgb = function(args) {
  return xyz2rgb(lab2xyz(args));
};

export const lch2lab = function(lch) {
  var l = lch[0],
      c = lch[1],
      h = lch[2],
      a, b, hr;

  hr = h / 360 * 2 * Math.PI;
  a = c * Math.cos(hr);
  b = c * Math.sin(hr);
  return [l, a, b];
};

export const lch2xyz = function(args) {
  return lab2xyz(lch2lab(args));
};

export const lch2rgb = function(args) {
  return lab2rgb(lch2lab(args));
};

export const rgb2hsl = function(rgb) {
  var r = rgb[0]/255,
      g = rgb[1]/255,
      b = rgb[2]/255,
      min = Math.min(r, g, b),
      max = Math.max(r, g, b),
      delta = max - min,
      h, s, l;

  if(max === min) {
    h = 0;
  }
  else if(r === max) {
    h = (g - b) / delta;
  }
  else if(g === max) {
    h = 2 + (b - r) / delta;
  }
  else if(b === max) {
    h = 4 + (r - g)/ delta;
  }

  h = Math.min(h * 60, 360);

  if(h < 0) {
    h += 360;
  }

  l = (min + max) / 2;

  if(max === min) {
    s = 0;
  }
  else if(l <= 0.5) {
    s = delta / (max + min);
  }
  else {
    s = delta / (2 - max - min);
  }

  return [h, s * 100, l * 100];
};

export const rgb2cmyk = function(rgb) {
  var r = rgb[0] / 255,
      g = rgb[1] / 255,
      b = rgb[2] / 255,
      c, m, y, k;

  k = Math.min(1 - r, 1 - g, 1 - b);
  c = (1 - r - k) / (1 - k) || 0;
  m = (1 - g - k) / (1 - k) || 0;
  y = (1 - b - k) / (1 - k) || 0;
  return [c * 100, m * 100, y * 100, k * 100];
};

export const rgb2xyz = function(rgb) {
  var r = rgb[0] / 255,
      g = rgb[1] / 255,
      b = rgb[2] / 255;

  // assume sRGB
  r = r > 0.04045 ? Math.pow(((r + 0.055) / 1.055), 2.4) : (r / 12.92);
  g = g > 0.04045 ? Math.pow(((g + 0.055) / 1.055), 2.4) : (g / 12.92);
  b = b > 0.04045 ? Math.pow(((b + 0.055) / 1.055), 2.4) : (b / 12.92);

  var x = (r * 0.4124) + (g * 0.3576) + (b * 0.1805);
  var y = (r * 0.2126) + (g * 0.7152) + (b * 0.0722);
  var z = (r * 0.0193) + (g * 0.1192) + (b * 0.9505);

  return [x * 100, y *100, z * 100];
};

export const rgb2lab = function(rgb) {
  var xyz = rgb2xyz(rgb),
        x = xyz[0],
        y = xyz[1],
        z = xyz[2],
        l, a, b;

  x /= 95.047;
  y /= 100;
  z /= 108.883;

  x = x > 0.008856 ? Math.pow(x, 1/3) : (7.787 * x) + (16 / 116);
  y = y > 0.008856 ? Math.pow(y, 1/3) : (7.787 * y) + (16 / 116);
  z = z > 0.008856 ? Math.pow(z, 1/3) : (7.787 * z) + (16 / 116);

  l = (116 * y) - 16;
  a = 500 * (x - y);
  b = 200 * (y - z);

  return [l, a, b];
};

export const rgb2lch = function(args) {
  return lab2lch(rgb2lab(args));
};

export const hsl2rgb = function(hsl) {
  var h = hsl[0] / 360,
      s = hsl[1] / 100,
      l = hsl[2] / 100,
      t1, t2, t3, rgb, val;

  if(s === 0) {
    val = l * 255;
    return [val, val, val];
  }

  if(l < 0.5) {
    t2 = l * (1 + s);
  }
  else {
    t2 = l + s - l * s;
  }
  t1 = 2 * l - t2;

  rgb = [0, 0, 0];
  for( let i = 0; i < 3; ++i) {
    t3 = h + 1 / 3 * - (i - 1);
    if( t3 < 0 ) { ++t3; }
    if( t3 > 1 ) { --t3; }

    if(6 * t3 < 1) {
      val = t1 + (t2 - t1) * 6 * t3;
    }
    else if(2 * t3 < 1) {
      val = t2;
    }
    else if(3 * t3 < 2) {
      val = t1 + (t2 - t1) * (2 / 3 - t3) * 6;
    }
    else {
      val = t1;
    }

    rgb[i] = val * 255;
  }

  return rgb;
};


export const cmyk2rgb = function(cmyk) {
  var c = cmyk[0] / 100,
      m = cmyk[1] / 100,
      y = cmyk[2] / 100,
      k = cmyk[3] / 100,
      r, g, b;

  r = 1 - Math.min(1, c * (1 - k) + k);
  g = 1 - Math.min(1, m * (1 - k) + k);
  b = 1 - Math.min(1, y * (1 - k) + k);
  return [r * 255, g * 255, b * 255];
};

export const cmyk2hsl = function(args) {
  return rgb2hsl(cmyk2rgb(args));
};

export const xyz2lch = function(args) {
  return lab2lch(xyz2lab(args));
};

export const interpolate_lab = function(ca,cb,x) {
  var ca_0 = ca[0], ca_1 = ca[1], ca_2 = ca[2];
  return [ ca_0 + ( cb[0]-ca_0 ) * x ,
           ca_1 + ( cb[1]-ca_1 ) * x ,
           ca_2 + ( cb[2]-ca_2 ) * x ];
};
export const interpolate_rgb = interpolate_lab;

var interpolate_h = function(ha,hb,x) {
  var dh = hb - ha;
  // shortest path
  if( dh > 180 ) {
    dh -= 360;
  }
  else if( dh < -180 ) {
    dh += 360;
  }
  return ha + dh * x;
};

export const interpolate_lch = function(ca,cb,x) {
  var ca_0 = ca[0], ca_1 = ca[1];
  return [ ca_0 + (cb[0]-ca_0) * x      ,
           ca_1 + (cb[1]-ca_1) * x      ,
           interpolate_h(ca[2],cb[2],x) ];
};


export const interpolate_hsl = function(ca,cb,x) {
  var ca_1 = ca[1], ca_2 = ca[2];
  return [ interpolate_h(ca[0],cb[0],x) ,
           ca_1 + (cb[1]-ca_1) * x      ,
           ca_2 + (cb[2]-ca_2) * x      ];

};



/** WEBPACK FOOTER **
 ** ./src/cdl/style/color-space.js
 **/